import React, { useEffect, useMemo, useState } from 'react';
import ApiService from '../../../../../../services/ApiService';
import { CandidateRow, InlineHint, LinkName, MiniButton, OpcoesNcm } from './style';

export interface DestinoNcm {
  ncmId: number;
  ncm: string;
}

interface Props {
  adotados: DestinoNcm[];
  onEscolher: (destino: DestinoNcm) => void;
}

interface ResultadoCatalogo {
  id: number;
  ncm: string;
  descricao: string | null;
}

const ATRASO_MS = 300;
const LIMITE = 20;

const ehResultado = (v: unknown): v is ResultadoCatalogo =>
  typeof v === 'object' && v !== null && 'id' in v && typeof v.id === 'number' && 'ncm' in v && typeof v.ncm === 'string';

const soDigitos = (v: string) => v.replace(/\D/g, '');

export default function NovoNcmSelect({ adotados, onEscolher }: Props): React.ReactElement {
  const [busca, setBusca] = useState('');
  const [catalogo, setCatalogo] = useState<ResultadoCatalogo[]>([]);

  useEffect(() => {
    const q = busca.trim();
    if (!q) {
      setCatalogo([]);
      return;
    }
    let ativo = true;
    const timer = setTimeout(() => {
      ApiService.stockLookup
        .searchNcmCatalogo(q, LIMITE, 0)
        .then((r: { data?: unknown }) => {
          if (!ativo) return;
          setCatalogo((Array.isArray(r?.data) ? r.data : []).filter(ehResultado));
        })
        .catch((error: unknown) => console.error('Erro ao buscar no catálogo de NCM:', error));
    }, ATRASO_MS);
    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [busca]);

  const opcoes = useMemo(() => {
    const digitos = soDigitos(busca);
    const daEmpresa = adotados
      .filter((a) => !digitos || soDigitos(a.ncm).includes(digitos))
      .map((a) => ({ ncmId: a.ncmId, ncm: a.ncm, descricao: catalogo.find((c) => c.id === a.ncmId)?.descricao ?? null, emUso: true }));
    const ids = new Set(adotados.map((a) => a.ncmId));
    const doCatalogo = catalogo
      .filter((c) => !ids.has(c.id))
      .map((c) => ({ ncmId: c.id, ncm: c.ncm, descricao: c.descricao, emUso: false }));
    return [...daEmpresa, ...doCatalogo];
  }, [adotados, catalogo, busca]);

  return (
    <>
      <input placeholder="Buscar novo NCM no catálogo" value={busca} onChange={(e) => setBusca(e.target.value)} />
      <InlineHint>Os NCMs da empresa vêm primeiro; o resto é o catálogo oficial.</InlineHint>
      <OpcoesNcm>
        {opcoes.length === 0 && <InlineHint>Nenhum NCM encontrado.</InlineHint>}
        {opcoes.map((o) => (
          <CandidateRow key={o.ncmId}>
            <LinkName as="div">
              <b>{o.ncm}</b>
              <span>
                {o.descricao ?? '—'}
                {o.emUso ? ' · em uso' : ''}
              </span>
            </LinkName>
            <MiniButton type="button" onClick={() => onEscolher({ ncmId: o.ncmId, ncm: o.ncm })}>
              Mover para este
            </MiniButton>
          </CandidateRow>
        ))}
      </OpcoesNcm>
    </>
  );
}
